import Cookies from "universal-cookie";
import url from "url";
import hostUtils from "./playerUnmount";

const cm = new Cookies();
const DEFAULT_AUTH_COOKIE_KEY = "authorization";
const LOCAL_CONSOLE_AUTH_COOKIE_KEY = "console_authorization";

const getConsoleAuthCookieKey = () => {
  if (typeof window === "undefined") {
    return DEFAULT_AUTH_COOKIE_KEY;
  }

  return hostUtils.isLocalDevHost(window.location?.hostname || "")
    ? LOCAL_CONSOLE_AUTH_COOKIE_KEY
    : DEFAULT_AUTH_COOKIE_KEY;
};

const getCookie = (key) => {
  return cm.get(key);
};

const setCookie = (key, value) => {
  cm.set(key, value, { path: "/", sameSite: "lax" });
};

const setCookies = (cookies, url) => {
  const keys = Object.keys(cookies);
  for (let i in keys) {
    const k = keys[i];
    const v = cookies[k];
    cm.set(k, v, { path: "/", sameSite: "lax"});
  }
};

const getCookies = (keys) => {
  const cookies = {};
  for (let i in keys) {
    const key = keys[i];
    cookies[key] = getCookie(key);
  }
  return cookies;
};

const getAuth = () => {
  return cm.get(getConsoleAuthCookieKey());
};

const setAuth = (auth) => {
  // access key 유효기간과 동일하게 12시간만 유지
  const expires = new Date(Date.now() + 1000 * 60 * 60 * 12);
  cm.set(getConsoleAuthCookieKey(), auth, { path: "/", sameSite: "lax", expires });
  hostUtils.storeConsoleAuthSnapshot(auth);
};

const getLogKey = () => {
  return cm.get("logKey");
};

const setLogKey = (logKey) => {
  // 같은 브라우저 탭 간 dedup: 이미 설정된 logKey가 있으면 덮어쓰지 않음
  // (탭 5개 열어도 첫 번째 탭의 logKey 유지 → 1명으로 카운트)
  // clearLogKey() 호출(로그아웃) 후에만 새 logKey 설정 가능
  if (cm.get("logKey")) return;
  // ✅ 7일 동안 유지 (브라우저 종료해도 유지)
  const expires = new Date();
  expires.setDate(expires.getDate() + 7);
  cm.set("logKey", logKey, { path: "/", sameSite: "lax", expires });
};

// 서버 응답 logKey 전용 — null/undefined 가드 포함 (Fix 8)
const forceSetLogKey = (logKey) => {
  if (!logKey || logKey === 'undefined') return;
  const expires = new Date();
  expires.setDate(expires.getDate() + 7);
  cm.set("logKey", logKey, { path: "/", sameSite: "lax", expires });
};

const clearAuth = (auth) => {
  cm.set(getConsoleAuthCookieKey(), "", { path: "/", sameSite: "lax" });
  hostUtils.clearConsoleAuthSnapshot();
};

const clearLogKey = () => {
  cm.set("logKey", "", { path: "/", sameSite: "lax" });
  // Phase 2: 로그아웃/재로그인 시 시청세션 ID(vsid_*)도 초기화 → 재로그인 = 새 세션(같은 탭 재로그인 병합 방지).
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      Object.keys(window.sessionStorage)
        .filter((k) => k.indexOf("vsid_") === 0)
        .forEach((k) => window.sessionStorage.removeItem(k));
    }
  } catch (e) {}
};

const getConsoleCountView = () => {
  return cm.get("consoleCountView");
};

const setConsoleCountView = (consoleCountView) => {
  cm.set("consoleCountView", consoleCountView, { path: "/", sameSite: "lax" });
};

// Phase 2: 시청세션 ID — 전역 쿠키 logKey 와 독립. (event,session) 스코프, sessionStorage(탭 단위).
//  - 같은 탭에서 새로고침/재입장해도 같은 시청은 동일 ID 유지(시청 안 쪼개짐).
//  - 탭/event/session 다르면 별도 ID. logKey churn(재로그인·다단말)에 면역.
//  - sessionStorage 불가 환경이면 null 반환 → 백엔드/갱신서버가 logKey 로 폴백.
const getViewingSessionId = (eventIdx, sessionIdx) => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const key = `vsid_${eventIdx}_${sessionIdx}`;
    let vsid = window.sessionStorage.getItem(key);
    if (!vsid) {
      vsid = "vs" + Math.random().toString(36).slice(2, 10) + Date.now();
      window.sessionStorage.setItem(key, vsid);
    }
    return vsid;
  } catch (e) {
    return null;
  }
};

const isLogin = () => {
  if (!getAuth()) {
    return false;
  }
  return true;
};

// Stored auth candidate only. Real validity is confirmed by /v1/client/iam.
const hasConsoleSession = () => {
  return hostUtils.matchesStoredConsoleAuth(getAuth());
};

const setCFCookies = (data) => {
  const { cfSign, cfUrl } = data;
  const { hostname: host } = url.parse(cfUrl);
  const names = host.split(".");
  let hostname = `.${host}`;
  if (names.length > 2) {
    hostname = `.${names[names.length - 2]}.${names[names.length - 1]}`;
  }
  const keys = Object.keys(cfSign);
  for (let i in keys) {
    const k = keys[i];
    const v = cfSign[k];
    cm.set(k, v, { path: "/", sameSite: "lax", domain: hostname });
  }
};

export default {
  set: setCookie,
  get: getCookie,
  setCookies,
  getCookies,
  getAuth,
  setAuth,
  clearAuth,
  hasConsoleSession,
  isLogin,
  setCFCookies,
  getLogKey,
  setLogKey,
  forceSetLogKey,
  clearLogKey,
  getConsoleCountView,
  setConsoleCountView,
  getViewingSessionId,
};
